import { isAxiosError } from "axios";
import { jwtDecode } from "jwt-decode";
import type { PostHogConfig } from "posthog-js";
import posthog from "posthog-js";

import {
  APP_ENV,
  APP_NAME,
  APP_SOURCE,
  APP_VERSION,
  GIT_SHA,
  POSTHOG_HOST,
  POSTHOG_KEY,
  siteSessionIdTokenKeyName,
} from "@/config/environment";
import { getCookie } from "@/tools/cookies";

const isEnabled = Boolean(POSTHOG_KEY);
const isProduction = APP_ENV === "prod";
const isTestEnvironment = APP_ENV === "test";
const IDENTIFY_PROPERTIES_STORAGE_PREFIX = "posthog.identify.properties.";
const IDENTIFIED_EMAIL_STORAGE_KEY = "posthog.identified_email";
const INTERNAL_USER_STORAGE_KEY = "posthog.is_internal";
const RECORDING_ACTIVE_KEY = "posthog.recording_active";
const TRACKING_ENABLED_KEY = "posthog.tracking_enabled";
const TRACKING_STATUS_KEY = "posthog.tracking_status";
const identifiedUserSignatures = new Map<string, string | undefined>();
let globalErrorListenersBound = false;
let currentIdentifiedEmail: string | undefined;
let currentIsInternalUser: boolean | undefined;
let currentTrackingEnabled: boolean | undefined;

const INTERNAL_EMAIL_DOMAIN = "@somosradar.com";
const AUTH_PATHS = new Set(["/sign-in", "/reset-password", "/forgot-password"]);
const IDENTITY_EVENTS = new Set(["$identify", "$set", "$set_once"]);
const SENSITIVE_PARAMS = new Set([
  "token",
  "code",
  "state",
  "session",
  "access_token",
  "refresh_token",
  "id_token",
  "auth",
  "key",
  "secret",
  "password",
]);
const SENSITIVE_PROPERTY_KEYS = new Set([
  ...SENSITIVE_PARAMS,
  "file_name",
  "filename",
  "original_filename",
  "authorization",
  "cookie",
  "set_cookie",
  "set-cookie",
]);
const URL_PROPS = [
  "$current_url",
  "$referrer",
  "$initial_current_url",
  "$initial_referrer",
  "$url",
] as const;

type PostHogBeforeSendEvent = {
  event?: string;
  properties?: Record<string, unknown>;
};

type PostHogConfigWithBeforeSend = Partial<PostHogConfig> & {
  before_send?: (
    event: PostHogBeforeSendEvent | null,
  ) => PostHogBeforeSendEvent | null;
  capture_exceptions: false;
  _capture_performance?: false;
};

const NOISE_MESSAGE_PATTERNS = [
  "Invalid path or write key",
  "@segment/analytics",
  "analytics.js",
];

function getExceptionMessages(
  properties?: Record<string, unknown> | null,
): string[] {
  const message = properties?.$exception_message;
  const exceptionList = properties?.$exception_list;
  const messages = typeof message === "string" ? [message] : [];
  if (Array.isArray(exceptionList)) {
    messages.push(
      ...exceptionList
        .map((exception) =>
          exception &&
          typeof exception === "object" &&
          "value" in exception &&
          typeof exception.value === "string"
            ? exception.value
            : undefined,
        )
        .filter((value): value is string => Boolean(value)),
    );
  }
  return messages;
}

function patchCaptureToFilterNoise(): void {
  const original = posthog.capture.bind(posthog);
  posthog.capture = (event, properties, options) => {
    if (event === "$exception") {
      const messages = getExceptionMessages(properties);
      if (
        messages.some((message) =>
          NOISE_MESSAGE_PATTERNS.some((pattern) => message.includes(pattern)),
        )
      )
        return undefined;
    }
    return original(event, properties, options);
  };
}

const DROPPED_AUTO_PROPERTIES = new Set([
  "$geoip_accuracy_radius",
  "$geoip_city_confidence",
  "$geoip_latitude",
  "$geoip_longitude",
  "$geoip_postal_code",
  "$geoip_subdivision_1_code",
  "$geoip_subdivision_1_name",
  "$geoip_subdivision_2_code",
  "$geoip_subdivision_2_name",
  "$raw_user_agent",
  "$referrer",
  "$referring_domain",
]);

function sanitizeProperties(
  properties: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(properties).filter(([key]) => {
      if (DROPPED_AUTO_PROPERTIES.has(key)) return false;
      if (key.startsWith("$geoip_")) return false;
      return true;
    }),
  );
}

function withoutQueryString(url?: string): string | undefined {
  return url?.split("?")[0];
}

function getAxiosErrorContext(error: unknown): Record<string, unknown> {
  if (!isAxiosError(error)) return {};

  const statusCode = error.response?.status;

  return {
    endpoint: withoutQueryString(error.config?.url),
    method: error.config?.method?.toUpperCase(),
    status_code: statusCode,
    status_label: statusCode
      ? String(statusCode)
      : "Network error / sin respuesta del backend",
    request_base_url: error.config?.baseURL,
  };
}

function getPostHogBaseProperties(): Record<string, string> {
  return {
    environment: APP_ENV,
    app: APP_NAME,
    source: APP_SOURCE,
    build_version: APP_VERSION,
    git_sha: GIT_SHA,
  };
}

function parseCapturedError(error: unknown): Error {
  if (error instanceof Error) return error;
  if (
    error &&
    typeof error === "object" &&
    "message" in error &&
    typeof error.message === "string"
  ) {
    return new Error(error.message);
  }
  return new Error(String(error));
}

function getVisibleExceptionFingerprint(
  context?: Record<string, unknown>,
): string | undefined {
  const fingerprint = context?.$exception_fingerprint;
  return typeof fingerprint === "string" ? fingerprint : undefined;
}

function getStableHash(value: string): string {
  let hash = 5381;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 33) ^ value.charCodeAt(index);
  }
  return (hash >>> 0).toString(36);
}

function getIdentifyPropertiesSignature(
  properties?: Record<string, string | boolean>,
): string | undefined {
  if (!properties) return undefined;
  const normalizedProperties = JSON.stringify(
    Object.fromEntries(
      Object.entries(properties).sort(([a], [b]) => a.localeCompare(b)),
    ),
  );
  return getStableHash(normalizedProperties);
}

function getIdentifyPropertiesStorageKey(userId: string): string {
  return `${IDENTIFY_PROPERTIES_STORAGE_PREFIX}${encodeURIComponent(userId)}`;
}

function readPersistedEmail(): string | undefined {
  try {
    return localStorage.getItem(IDENTIFIED_EMAIL_STORAGE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

function persistIdentifiedEmail(email: string | undefined): void {
  try {
    if (email) {
      localStorage.setItem(IDENTIFIED_EMAIL_STORAGE_KEY, email);
    } else {
      localStorage.removeItem(IDENTIFIED_EMAIL_STORAGE_KEY);
    }
  } catch {
    // Ignore storage failures.
  }
}

function readPersistedInternalUser(): boolean | undefined {
  try {
    const value = localStorage.getItem(INTERNAL_USER_STORAGE_KEY);
    if (value === null) return undefined;
    return value === "true";
  } catch {
    return undefined;
  }
}

function persistInternalUser(value: boolean | undefined): void {
  try {
    if (typeof value === "boolean") {
      localStorage.setItem(INTERNAL_USER_STORAGE_KEY, String(value));
    } else {
      localStorage.removeItem(INTERNAL_USER_STORAGE_KEY);
    }
  } catch {
    // Ignore storage failures.
  }
}

function readPersistedTrackingEnabled(): boolean | undefined {
  try {
    const value = localStorage.getItem(TRACKING_ENABLED_KEY);
    if (value === null) return undefined;
    return value === "true";
  } catch {
    return undefined;
  }
}

function persistTrackingEnabled(value: boolean | undefined): void {
  try {
    if (typeof value === "boolean") {
      localStorage.setItem(TRACKING_ENABLED_KEY, String(value));
    } else {
      localStorage.removeItem(TRACKING_ENABLED_KEY);
    }
  } catch {
    // Ignore storage failures.
  }
}

function persistTrackingStatus(trackingEnabled: boolean | undefined): void {
  try {
    const status =
      trackingEnabled === true
        ? "allowed"
        : trackingEnabled === false
          ? "blocked_missing_posthog_tracking_group"
          : "blocked_no_tracking_decision";
    const diagnostic = {
      environment: APP_ENV,
      status,
      tracking_enabled: trackingEnabled === true,
      updated_at: new Date().toISOString(),
    };

    localStorage.setItem(TRACKING_STATUS_KEY, JSON.stringify(diagnostic));

    if (!isProduction) {
      console.info("[PostHog tracking]", diagnostic);
    }
  } catch {
    // Ignore storage/logging failures.
  }
}

function clearTrackingStatus(): void {
  try {
    localStorage.removeItem(TRACKING_STATUS_KEY);
  } catch {
    // Ignore storage failures.
  }
}

function isRecordingActive(): boolean {
  try {
    return localStorage.getItem(RECORDING_ACTIVE_KEY) === "true";
  } catch {
    return false;
  }
}

function setRecordingFlag(): void {
  try {
    localStorage.setItem(RECORDING_ACTIVE_KEY, "true");
  } catch {
    // Ignore storage failures.
  }
}

function clearRecordingFlag(): void {
  try {
    localStorage.removeItem(RECORDING_ACTIVE_KEY);
  } catch {
    // Ignore storage failures.
  }
}

function normalizeEmail(value: unknown): string | undefined {
  return typeof value === "string" ? value.trim().toLowerCase() : undefined;
}

function normalizeBoolean(value: unknown): boolean | undefined {
  return typeof value === "boolean" ? value : undefined;
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function getEventTrackingEnabled(
  properties?: Record<string, unknown>,
): boolean | undefined {
  const setProperties = properties?.$set;
  const setOnceProperties = properties?.$set_once;

  return (
    normalizeBoolean(properties?.posthog_tracking_enabled) ??
    (isPlainRecord(setProperties) && "posthog_tracking_enabled" in setProperties
      ? normalizeBoolean(setProperties.posthog_tracking_enabled)
      : undefined) ??
    (isPlainRecord(setOnceProperties) &&
    "posthog_tracking_enabled" in setOnceProperties
      ? normalizeBoolean(setOnceProperties.posthog_tracking_enabled)
      : undefined)
  );
}

function getEventInternalUser(
  properties?: Record<string, unknown>,
): boolean | undefined {
  const setProperties = properties?.$set;
  const setOnceProperties = properties?.$set_once;

  return (
    normalizeBoolean(properties?.is_internal) ??
    (isPlainRecord(setProperties) && "is_internal" in setProperties
      ? normalizeBoolean(setProperties.is_internal)
      : undefined) ??
    (isPlainRecord(setOnceProperties) && "is_internal" in setOnceProperties
      ? normalizeBoolean(setOnceProperties.is_internal)
      : undefined)
  );
}

function resolveTrackingEnabled(
  properties?: Record<string, unknown>,
): boolean | undefined {
  return getEventTrackingEnabled(properties) ?? currentTrackingEnabled;
}

function isInternalUser(properties?: Record<string, unknown>): boolean {
  return Boolean(
    getEventInternalUser(properties) ??
      currentIsInternalUser ??
      readPersistedInternalUser(),
  );
}

function isTrackingEnabled(properties?: Record<string, unknown>): boolean {
  return resolveTrackingEnabled(properties) === true;
}

function shouldDropUserEvents(properties?: Record<string, unknown>): boolean {
  // A real end user never runs a local build against prod; any prod event with
  // build_version "local" is a developer testing locally, so drop it.
  if (isProduction && APP_VERSION === "local") return true;
  if (isProduction) return isInternalUser(properties);
  if (isTestEnvironment) return !isTrackingEnabled(properties);
  return false;
}

function isAuthPath(value: unknown): boolean {
  if (typeof value !== "string") return false;

  try {
    const url = new URL(value, window.location.origin);
    return AUTH_PATHS.has(url.pathname);
  } catch {
    return AUTH_PATHS.has(value.split("?")[0]);
  }
}

function isAuthPage(properties?: Record<string, unknown>): boolean {
  return (
    AUTH_PATHS.has(window.location.pathname) ||
    URL_PROPS.some((prop) => isAuthPath(properties?.[prop]))
  );
}

function sanitizeUrl(value: unknown): unknown {
  if (typeof value !== "string") return value;

  try {
    const isAbsoluteUrl = /^[a-z][a-z\d+\-.]*:\/\//i.test(value);
    const url = new URL(value, window.location.origin);
    SENSITIVE_PARAMS.forEach((param) => {
      url.searchParams.delete(param);
    });
    const sanitizedPath = `${url.pathname}${url.search}${url.hash}`;

    return isAbsoluteUrl ? `${url.origin}${sanitizedPath}` : sanitizedPath;
  } catch {
    return value.split("?")[0];
  }
}

function sanitizeStringValue(value: string): string {
  const looksLikeUrl =
    value.includes("?") ||
    value.startsWith("/") ||
    /^[a-z][a-z\d+\-.]*:\/\//i.test(value);

  return looksLikeUrl ? String(sanitizeUrl(value)) : value;
}

function isSensitivePropertyKey(key: string): boolean {
  const normalizedKey = key.trim().toLowerCase();

  return (
    SENSITIVE_PROPERTY_KEYS.has(normalizedKey) ||
    normalizedKey.endsWith("_token") ||
    normalizedKey.endsWith("_secret") ||
    normalizedKey.endsWith("_password") ||
    normalizedKey.includes("authorization")
  );
}

function sanitizePropertyValue(key: string, value: unknown): unknown {
  if (isSensitivePropertyKey(key)) return "[REDACTED]";
  if (typeof value === "string") return sanitizeStringValue(value);
  if (Array.isArray(value)) {
    return value.map((item) => sanitizePropertyValue(key, item));
  }
  if (isPlainRecord(value)) return sanitizeEventProperties(value);
  return value;
}

function sanitizeEventProperties(
  properties: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(properties).map(([key, value]) => [
      key,
      sanitizePropertyValue(key, value),
    ]),
  );
}

function sanitizeIdentifyProperties(
  properties?: Record<string, string | boolean>,
): Record<string, string | boolean> | undefined {
  if (!properties) return undefined;

  return Object.fromEntries(
    Object.entries(properties).filter(([key]) => {
      const normalizedKey = key.trim().toLowerCase();
      return !["name", "user_email", "useremail"].includes(normalizedKey);
    }),
  );
}

function getUserEmailProperties(): Record<string, string> {
  const email = currentIdentifiedEmail ?? readPersistedEmail();
  return email ? { user_email: email } : {};
}

function beforeSendPostHogEvent(
  event: PostHogBeforeSendEvent | null,
): PostHogBeforeSendEvent | null {
  if (!event) return null;

  if (shouldDropUserEvents(event.properties)) {
    return null;
  }

  if (
    event.event &&
    IDENTITY_EVENTS.has(event.event) &&
    isAuthPage(event.properties)
  ) {
    return null;
  }

  event.properties = event.properties
    ? sanitizeEventProperties(event.properties)
    : event.properties;

  return event;
}

function hasSentIdentifyProperties(
  userId: string,
  signature: string | undefined,
): boolean {
  if (!signature) return true;
  try {
    return (
      window.localStorage.getItem(getIdentifyPropertiesStorageKey(userId)) ===
      signature
    );
  } catch {
    return false;
  }
}

function rememberIdentifyProperties(
  userId: string,
  signature: string | undefined,
): void {
  if (!signature) return;
  try {
    window.localStorage.setItem(
      getIdentifyPropertiesStorageKey(userId),
      signature,
    );
  } catch {
    // Ignore storage failures; identification should still work without persistence.
  }
}

function bindGlobalErrorListeners(): void {
  if (globalErrorListenersBound) return;

  window.addEventListener("error", (event) => {
    captureError(event.error ?? new Error(event.message), {
      error_source: "window_error",
      error_type: "window.error",
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    captureError(event.reason, {
      error_source: "unhandledrejection",
      error_type: "unhandledrejection",
    });
  });

  globalErrorListenersBound = true;
}

// Primes the identity (email + internal flag) straight from the session cookie
// at init time, before any query can run or any error can be captured. Without
// this, identity is only resolved later inside the fetchUserData query, so an
// early failure (e.g. a network error while the page mounts) races ahead of it
// and gets captured as an anonymous event — bypassing the internal-user filter.
function primeIdentityFromSession(): void {
  try {
    const idToken = getCookie(siteSessionIdTokenKeyName);
    if (!idToken) return;
    const { email } = jwtDecode<{ email?: string }>(idToken);
    const normalizedEmail = normalizeEmail(email);
    if (!normalizedEmail) return;

    currentIdentifiedEmail = normalizedEmail;
    currentIsInternalUser = normalizedEmail.endsWith(INTERNAL_EMAIL_DOMAIN);
    persistIdentifiedEmail(currentIdentifiedEmail);
    persistInternalUser(currentIsInternalUser);
    posthog.register({ user_email: currentIdentifiedEmail });
  } catch {
    // If the token is missing or undecodable, identity still resolves later
    // via identifyUser; priming is a best-effort head start.
  }
}

export function initPostHog(): void {
  if (!isEnabled) return;

  currentIdentifiedEmail = readPersistedEmail();
  currentIsInternalUser = readPersistedInternalUser();
  if (!isTestEnvironment) {
    currentTrackingEnabled = readPersistedTrackingEnabled();
  }

  const config: PostHogConfigWithBeforeSend = {
    api_host: POSTHOG_HOST,
    person_profiles: "identified_only",
    autocapture: false,
    capture_exceptions: false,
    capture_pageview: false,
    capture_pageleave: false,
    disable_session_recording: true,
    debug: false,
    mask_all_element_attributes: true,
    mask_all_text: true,
    sanitize_properties: sanitizeProperties,
    session_recording: {
      maskAllInputs: true,
    },
    before_send: beforeSendPostHogEvent,
    ...(isProduction
      ? {
          _capture_performance: false,
          disable_surveys: true,
          enable_heatmaps: false,
        }
      : {}),
  };
  posthog.init(POSTHOG_KEY!, config);
  posthog.register(getPostHogBaseProperties());
  primeIdentityFromSession();
  patchCaptureToFilterNoise();
  bindGlobalErrorListeners();

  if (isRecordingActive() && !isTestEnvironment) {
    posthog.startSessionRecording();
  }
}

export function identifyUser(
  userId: string,
  properties?: Record<string, string | boolean>,
): void {
  if (!isEnabled) return;

  currentIdentifiedEmail = normalizeEmail(properties?.email);
  currentIsInternalUser =
    normalizeBoolean(properties?.is_internal) ??
    currentIdentifiedEmail?.endsWith(INTERNAL_EMAIL_DOMAIN);
  currentTrackingEnabled = normalizeBoolean(
    properties?.posthog_tracking_enabled,
  );
  persistIdentifiedEmail(currentIdentifiedEmail);
  persistInternalUser(currentIsInternalUser);
  persistTrackingEnabled(currentTrackingEnabled);
  persistTrackingStatus(currentTrackingEnabled);

  if (shouldDropUserEvents(properties)) {
    clearRecordingFlag();
    return;
  }

  const identifyProperties = sanitizeIdentifyProperties(properties);
  const propertiesSignature =
    getIdentifyPropertiesSignature(identifyProperties);
  if (
    identifiedUserSignatures.has(userId) &&
    identifiedUserSignatures.get(userId) === propertiesSignature
  ) {
    return;
  }

  if (currentIdentifiedEmail) {
    posthog.register({ user_email: currentIdentifiedEmail });
  }

  if (hasSentIdentifyProperties(userId, propertiesSignature)) {
    posthog.identify(userId);
  } else {
    posthog.identify(userId, identifyProperties);
    rememberIdentifyProperties(userId, propertiesSignature);
  }
  identifiedUserSignatures.set(userId, propertiesSignature);

  posthog.startSessionRecording();
  setRecordingFlag();
}

export const EVENTS = {
  CONCILIATION_ENGINE_TRIGGERED: "conciliation_engine_triggered",
  MATCH_APPROVED: "match_approved",
  MATCH_REJECTED: "match_rejected",
  MATCH_CREATED: "match_created",
  MATCH_DELETED: "match_deleted",
  MATCH_DETAIL_OPENED: "match_detail_opened",
  MOVEMENT_DETAIL_OPENED: "movement_detail_opened",
  MOVEMENT_FILTERED: "movement_filtered",
  MOVEMENT_EXPORTED: "movement_exported",
  FILE_UPLOADED: "file_uploaded",
  FILE_PROCESSED: "file_processed",
  SOURCE_VIEWED: "source_viewed",
  SOURCE_EXPORTED: "source_exported",
} as const;

export type EventName = (typeof EVENTS)[keyof typeof EVENTS];

export function captureEvent(
  event: EventName,
  properties?: Record<string, unknown>,
): void {
  if (!isEnabled) return;
  if (isProduction) return;
  if (shouldDropUserEvents(properties)) return;
  posthog.capture(
    event,
    properties ? sanitizeEventProperties(properties) : properties,
  );
}

export function resetPostHogUser(): void {
  if (!isEnabled) return;
  currentIdentifiedEmail = undefined;
  currentIsInternalUser = undefined;
  currentTrackingEnabled = undefined;
  persistIdentifiedEmail(undefined);
  persistInternalUser(undefined);
  persistTrackingEnabled(undefined);
  clearTrackingStatus();
  clearRecordingFlag();
  posthog.reset();
}

export function captureError(
  error: unknown,
  context?: Record<string, unknown>,
): void {
  if (!isEnabled) return;
  if (isAxiosError(error) && error.response?.status === 401) return;
  if (shouldDropUserEvents()) return;
  const parsedError = parseCapturedError(error);
  posthog.captureException(parsedError, {
    ...sanitizeEventProperties(context ?? {}),
    exception_fingerprint: getVisibleExceptionFingerprint(context),
    exception_message: parsedError.message,
    exception_stack: parsedError.stack,
    exception_type: parsedError.name,
    ...getPostHogBaseProperties(),
    ...getUserEmailProperties(),
    url: sanitizeStringValue(window.location.href),
    ...getAxiosErrorContext(error),
  });
}
